/**
 * Utility functions for handling name formatting and validation
 */
import * as Yup from 'yup';

/**
 * Capitalizes the first letter of each word in a name
 * Handles special cases like hyphens, apostrophes, and spaces
 * @param name - The name to capitalize
 * @returns The properly capitalized name
 */
export function capitalizeNames(name: string): string {
  if (!name || typeof name !== 'string') {
    return name;
  }

  return name
    .toLowerCase()
    .split(/(\s+|[-'´`]+)/) // Split on spaces, hyphens, and apostrophes but keep delimiters
    .map((part, index, array) => {
      // If it's a delimiter (space, hyphen, apostrophe), return as-is
      if (/^(\s+|[-'´`]+)$/.test(part)) {
        return part;
      }
      
      // If it's a word part, capitalize the first letter
      if (part.length > 0) {
        return part.charAt(0).toUpperCase() + part.slice(1);
      }
      
      return part;
    })
    .join('');
}

/**
 * Updated name validation regex that accepts any letters and will be auto-capitalized
 * This is more lenient and user-friendly
 */
export const FLEXIBLE_NAME_VALIDATION_REGEX = /^[a-zA-Z\s'´`-]+$/;

/**
 * Validates if a name contains only valid characters (letters, spaces, hyphens, apostrophes)
 * @param name - The name to validate
 * @returns true if valid, false otherwise
 */
export function isValidNameFormat(name: string): boolean {
  if (!name || typeof name !== 'string') {
    return false;
  }
  
  const trimmedName = name.trim();
  
  // Must have at least one character and only contain valid name characters
  return trimmedName.length > 0 && FLEXIBLE_NAME_VALIDATION_REGEX.test(trimmedName);
}

/**
 * Creates a Yup validation schema for names that auto-capitalizes
 * @param fieldName - The name of the field for error messages
 * @param isRequired - Whether the field is required
 * @returns Yup schema with auto-capitalization
 */
export function createNameValidationSchema(fieldName: string, isRequired: boolean = true) {
  let schema = Yup.string()
    .trim()
    .transform((value) => {
      // Auto-capitalize the name
      return capitalizeNames(value);
    })
    .test(
      'valid-name-format',
      `${fieldName} must contain only letters, spaces, hyphens, and apostrophes`,
      (value) => {
        if (!value) return !isRequired; // Allow empty if not required
        return isValidNameFormat(value);
      }
    );

  if (isRequired) {
    schema = schema.required(`Please enter ${fieldName.toLowerCase()}`);
  }

  return schema;
}
